import classNames from 'classnames';
import { getSlug } from '@lib/utils';
import { useRouter } from 'next/router';
import { useSelector } from 'react-redux';

import Button from '@components/button';
import Icon from '@components/icon';
import Link from '@components/link';
import EnhancedContactForm from '@components/forms/enhanced';
import FormRealtorCards from '@components/forms/enhanced/realtor-cards';
import BrokerReciprocityCard from '@components/broker-reciprocity-card';
import { useListingContext } from '../../context';

import {
  overview_heading,
  summary,
  concession_note_container,
  seller_concession_note,
  directions_container,
  directions_link,
  refresh_date,
  buttons,
  mobile_button,
  center
} from './styles.module.scss';

const PropertySummary = ({
  setNotificationModalIsOpen,
  setNotesModalIsOpen,
  setShareModalIsOpen,
  setPreApprovalModalIsOpen,
  setRequestATourModalIsOpen
}) => {
  const router = useRouter();
  const { saved, authenticated: isLoggedIn } = useSelector((state) => state.user);

  const {
    address,
    marketingRemarks,
    statusId,
    sellerConcessions,
    primaryDisplayAgent,
    associatedAgent,
    directions,
    hasMapLocationExternalLink,
    mapLocationExternalLink,
    dataRefreshed,
    shouldShowSibcyAgent,
    secondaryDisplayAgent,
    hasTour,
    hasSecondaryAgent,
    informationRequestDefaultComments,
    listingId: id,
    canonicalUrl
  } = useListingContext();

  const savedListing = saved.find(({ listingId }) => id === listingId);

  const handleOpenNotificationModal = () => {
    if (isLoggedIn) {
      setNotificationModalIsOpen(true);
    } else {
      if (typeof window !== 'undefined') {
        window.location.href = '/api/auth/login';
      } else {
        router.push('/api/auth/login');
      }
    }
  };

  const handleOpenNotesModal = () => {
    if (isLoggedIn) {
      setNotesModalIsOpen(true);
    } else {
      if (typeof window !== 'undefined') {
        window.location.href = '/api/auth/login';
      } else {
        router.push('/api/auth/login');
      }
    }
  };

  const shareButtons = (
    <div className={buttons}>
      <span
        className={mobile_button}
        onClick={handleOpenNotificationModal}
        onKeyDown={handleOpenNotificationModal}
        role="button"
        tabIndex="0"
      >
        <Button
          icon="notification"
          iconType="actions"
          label="Notify Me"
          theme="text-only"
          onClick={handleOpenNotificationModal}
        />
      </span>
      <span
        className={mobile_button}
        onClick={handleOpenNotesModal}
        onKeyDown={handleOpenNotesModal}
        role="button"
        tabIndex="0"
      >
        <Button
          icon="notes"
          iconType="actions"
          label="Add Note"
          theme="text-only"
          onClick={handleOpenNotesModal}
        />
      </span>
      <span
        className={mobile_button}
        onClick={() => setShareModalIsOpen(true)}
        onKeyDown={() => setShareModalIsOpen(true)}
        role="button"
        tabIndex="0"
      >
        <Button
          icon="share"
          iconType="actions"
          label="Share"
          theme="text-only"
          onClick={() => setShareModalIsOpen(true)}
        />
      </span>
    </div>
  );

  return (
    <>
      <h2 className={overview_heading}>More About {address}</h2>

      <div className={summary}>
        {marketingRemarks}

        <div className={concession_note_container}>
          {statusId === 6 ? (
            <>
              <Button
                label="Get Property Estimate"
                url={`https://homevalue.sibcycline.com/${
                  primaryDisplayAgent?.agentId ||
                  router?.query?.agentid ||
                  associatedAgent?.agentId ||
                  ''
                }`}
              />
              <div className={seller_concession_note}>
                <Icon type="general" name="flame"></Icon>
                <p>How does your home compare?</p>
              </div>
            </>
          ) : (
            <>
              <Button label="Get Pre-Approved" onClick={setPreApprovalModalIsOpen} />
              <div className={seller_concession_note}>
                <Icon type="general" name="flame"></Icon>
                <p>
                  {sellerConcessions
                    ? sellerConcessions
                    : 'Connect with a loan officer to get started!'}
                </p>
              </div>
            </>
          )}
        </div>

        {(directions || hasMapLocationExternalLink) && statusId != 6 && (
          <div className={directions_container}>
            {hasMapLocationExternalLink ? (
              <Link url={mapLocationExternalLink} target="_blank" className={directions_link}>
                <Icon type="filters" name="location"></Icon>
                <h3>Directions to this Listing</h3>
              </Link>
            ) : (
              <h3>Directions to this Listing</h3>
            )}
            <p>{directions && `: ${directions}`}</p>
          </div>
        )}
        {dataRefreshed && (
          <div className={refresh_date}>
            <p>Information Refreshed: {dataRefreshed}</p>
          </div>
        )}
        {shareButtons}
      </div>

      <div className={center}>
        {shouldShowSibcyAgent ? (
          <EnhancedContactForm
            primaryAgent={primaryDisplayAgent}
            secondaryAgent={secondaryDisplayAgent}
            defaultFormValue={informationRequestDefaultComments}
            hasTour={hasTour}
            hasSecondaryAgent={hasSecondaryAgent}
            listingId={id}
            agentId="0"
            sticky={true}
            setModalIsOpen={setRequestATourModalIsOpen}
            endpoint="property information request"
          >
            <FormRealtorCards
              hasTour={hasTour}
              hasSecondaryAgent={hasSecondaryAgent}
              setModalIsOpen={setRequestATourModalIsOpen}
              primaryContact={primaryDisplayAgent}
              secondaryContact={secondaryDisplayAgent}
            />
          </EnhancedContactForm>
        ) : !associatedAgent ? (
          <BrokerReciprocityCard
            hasTour={hasTour}
            listingId={id}
            defaultFormValue={informationRequestDefaultComments}
            sticky={false}
            setModalIsOpen={setRequestATourModalIsOpen}
          />
        ) : (
          <EnhancedContactForm
            hasTour={hasTour}
            setModalIsOpen={setRequestATourModalIsOpen}
            primaryAgent={primaryDisplayAgent || associatedAgent}
            sticky={false}
            listingId={id}
            agentId={associatedAgent.agentId}
            defaultFormValue={informationRequestDefaultComments}
            endpoint="property information request"
          >
            <FormRealtorCards
              hasTour={hasTour}
              primaryContact={associatedAgent}
              setModalIsOpen={setRequestATourModalIsOpen}
            />
          </EnhancedContactForm>
        )}
      </div>
    </>
  );
};

export default PropertySummary;
